// Native WebRTC 1:1 in-app audio calling.
//
// Signaling rides on the existing SignalR hub (Offer/SendAnswer/
// SendIceCandidate/SendCallEnd, with ReceiveOffer/ReceiveAnswer/ReceiveIceCandidate/
// ReceiveCallEnd/ReceiveCallId/ReceiveCallDecline/ReceiveUserBusy coming back.
// The signaling invoke functions are injected via setSignaling() so this module has
// no import cycle with signalRService.js.
//
// ICE servers (STUN + TURN with short-lived credentials) come from Twilio's Network
// Traversal Service via the backend GET /api/Call/IceServers. We fetch them lazily and
// cache until their TTL expires. If the fetch fails we fall back to public STUN only —
// which works on the same LAN / lenient networks but not across strict NATs/firewalls.
import { getIceServers } from "../api/apiManager";

export const WEBRTC_STATUS = {
  IDLE: "Idle",
  CALLING: "Calling...",
  RINGING: "Ringing...",
  CONNECTED: "Connected",
  DISCONNECTED: "Call Ended",
  FAILED: "Failed",
  BUSY: "Busy",
  NO_ANSWER: "No Answer",
};

// Used only if the Twilio ICE-server fetch fails.
const FALLBACK_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

class WebRtcCallService {
  constructor() {
    this.pc = null;
    this.localStream = null;
    this.remoteAudioEl = null;
    this.callId = null;
    this.peerUserId = null; // the other party's app userId
    this.selfUserId = null;
    this.role = null; // "caller" | "callee"
    this.pendingOffer = null; // { callId, callerId, offer, callType }
    this.pendingRemoteIce = []; // ICE that arrived before remoteDescription was set
    this.pendingLocalIce = []; // ICE generated before we know callId (caller side)
    this.status = WEBRTC_STATUS.IDLE;
    this.listeners = new Set();
    this._signaling = null;
    this._iceServers = null; // cached Twilio ICE servers
    this._iceExpiresAt = 0; // epoch ms when the cached creds expire
  }

  // Resolve ICE servers from Twilio (cached until TTL), falling back to STUN-only.
  async _resolveIceServers() {
    const now = Date.now();
    if (this._iceServers && now < this._iceExpiresAt) return this._iceServers;
    try {
      const res = await getIceServers();
      const servers = res?.data?.iceServers;
      if (Array.isArray(servers) && servers.length > 0) {
        this._iceServers = servers;
        const ttlSec = parseInt(res.data.ttl, 10) || 3600;
        // Refresh a minute early so creds never expire mid-setup.
        this._iceExpiresAt = now + Math.max(0, ttlSec - 60) * 1000;
        return this._iceServers;
      }
      console.warn("[WebRTC] no ICE servers from backend; using STUN fallback");
    } catch (e) {
      console.error(
        "[WebRTC] ICE server fetch failed; using STUN fallback:",
        e,
      );
    }
    return FALLBACK_ICE_SERVERS;
  }

  // { sendOffer, sendAnswer, sendIce, sendEnd } — wired once from signalRService.
  setSignaling(signaling) {
    this._signaling = signaling;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(payload) {
    this.listeners.forEach((fn) => fn(payload));
  }

  setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this.emit({ type: "status", status });
  }

  getState() {
    return {
      status: this.status,
      callId: this.callId,
      peerUserId: this.peerUserId,
      isActive: !!this.pc,
    };
  }

  isBusy() {
    return !!this.pc;
  }

  // ---- media / peer connection ----
  _ensureRemoteAudioEl() {
    if (this.remoteAudioEl) return this.remoteAudioEl;
    const el = document.createElement("audio");
    el.autoplay = true;
    el.id = "webrtc-remote-audio";
    document.body.appendChild(el);
    this.remoteAudioEl = el;
    return el;
  }

  async _createPeerConnection() {
    const iceServers = await this._resolveIceServers();
    const pc = new RTCPeerConnection({ iceServers });

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      const candidate = JSON.stringify(event.candidate);
      if (this.callId) {
        this._signaling?.sendIce(this.callId, this.selfUserId, candidate);
      } else {
        // Caller may generate ICE before ReceiveCallId arrives — buffer it.
        this.pendingLocalIce.push(candidate);
      }
    };

    pc.ontrack = (event) => {
      const el = this._ensureRemoteAudioEl();
      el.srcObject = event.streams[0];
    };

    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected") this.setStatus(WEBRTC_STATUS.CONNECTED);
      if (st === "failed") {
        this.setStatus(WEBRTC_STATUS.FAILED);
        this.cleanup();
      }
      if (st === "disconnected" || st === "closed") {
        this.setStatus(WEBRTC_STATUS.DISCONNECTED);
      }
    };

    this.pc = pc;
    return pc;
  }

  async _getMic() {
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });
    return this.localStream;
  }

  _flushLocalIce() {
    if (!this.callId || this.pendingLocalIce.length === 0) return;
    this.pendingLocalIce.forEach((candidate) =>
      this._signaling?.sendIce(this.callId, this.selfUserId, candidate),
    );
    this.pendingLocalIce = [];
  }

  async _flushRemoteIce() {
    if (!this.pc || this.pendingRemoteIce.length === 0) return;
    for (const candidate of this.pendingRemoteIce) {
      try {
        await this.pc.addIceCandidate(
          new RTCIceCandidate(JSON.parse(candidate)),
        );
      } catch (e) {
        console.error("[WebRTC] addIceCandidate (flush) failed:", e);
      }
    }
    this.pendingRemoteIce = [];
  }

  // ---- caller ----
  async startCall({ callerId, calleeUserId, chatId, callType = "Audio" }) {
    if (this.isBusy()) {
      console.warn("[WebRTC] already in a call; ignoring new startCall");
      return;
    }
    this.role = "caller";
    this.selfUserId = parseInt(callerId);
    this.peerUserId = parseInt(calleeUserId);
    this.callId = null;
    this.pendingLocalIce = [];
    this.pendingRemoteIce = [];
    this.setStatus(WEBRTC_STATUS.CALLING);

    const pc = await this._createPeerConnection();
    const stream = await this._getMic();
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);

    this._signaling?.sendOffer(
      this.selfUserId,
      this.peerUserId,
      chatId ? parseInt(chatId) : null,
      callType,
      JSON.stringify(offer),
    );
  }

  // Caller learns the server-assigned callId; flush any buffered ICE.
  onCallId(callId) {
    if (this.role !== "caller") return;
    this.callId = parseInt(callId);
    this._flushLocalIce();
  }

  onCallDeclined() {
    this.setStatus(WEBRTC_STATUS.NO_ANSWER);
    this.cleanup();
  }

  onUserBusy() {
    this.setStatus(WEBRTC_STATUS.BUSY);
    this.cleanup();
  }

  async onAnswer({ callId, answer }) {
    if (!this.pc) return;
    if (this.callId && parseInt(callId) !== this.callId) return;
    try {
      await this.pc.setRemoteDescription(
        new RTCSessionDescription(JSON.parse(answer)),
      );
      this.setStatus(WEBRTC_STATUS.RINGING);
      await this._flushRemoteIce();
    } catch (e) {
      console.error("[WebRTC] setRemoteDescription(answer) failed:", e);
    }
  }

  // ---- callee ----
  // Store the incoming offer and surface it to the UI. The UI calls accept()/reject().
  onIncomingOffer({ callId, callerId, offer, callType }) {
    if (this.isBusy()) {
      // Already on a call — tell the caller we're busy. (Hub broadcasts; caller filters.)
      return;
    }
    this.pendingOffer = {
      callId: parseInt(callId),
      callerId: parseInt(callerId),
      offer,
      callType,
    };
    // Capture our own id as soon as the offer arrives. accept() normally sets this, but a
    // straight decline never calls accept() — without it reject() would send NaN -> null
    // and the hub's non-nullable `long UserId` binding would fail ("Failed to invoke ...").
    if (this.selfUserId == null) {
      const uid = parseInt(localStorage.getItem("user_id"));
      if (!Number.isNaN(uid)) this.selfUserId = uid;
    }
    this.emit({
      type: "incoming",
      callId: parseInt(callId),
      callerId: parseInt(callerId),
      callType,
    });
  }

  async accept({ selfUserId } = {}) {
    const pending = this.pendingOffer;
    if (!pending) return;
    this.role = "callee";
    this.callId = pending.callId;
    this.peerUserId = pending.callerId;
    this.selfUserId = parseInt(selfUserId ?? this.selfUserId);
    this.pendingOffer = null;
    this.setStatus(WEBRTC_STATUS.CALLING);

    const pc = await this._createPeerConnection();
    const stream = await this._getMic();
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    await pc.setRemoteDescription(
      new RTCSessionDescription(JSON.parse(pending.offer)),
    );
    await this._flushRemoteIce();

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    this._signaling?.sendAnswer(
      this.callId,
      this.selfUserId,
      JSON.stringify(answer),
    );
    this.setStatus(WEBRTC_STATUS.CONNECTED);
  }

  reject({ selfUserId } = {}) {
    const pending = this.pendingOffer;
    this.pendingOffer = null;
    if (pending) {
      // Fall back to the logged-in user id so SendCallEnd always binds, even if the call
      // was declined before it was ever accepted (selfUserId would otherwise be null).
      const uid = parseInt(
        selfUserId ?? this.selfUserId ?? localStorage.getItem("user_id"),
      );
      this._signaling?.sendEnd(pending.callId, uid);
    }
    this.setStatus(WEBRTC_STATUS.DISCONNECTED);
  }

  // ---- shared ----
  async onRemoteIce({ callId, candidate }) {
    if (this.callId && parseInt(callId) !== this.callId) return; // not our call
    if (!this.pc || !this.pc.remoteDescription) {
      this.pendingRemoteIce.push(candidate);
      return;
    }
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(JSON.parse(candidate)));
    } catch (e) {
      console.error("[WebRTC] addIceCandidate failed:", e);
    }
  }

  onRemoteEnd({ callId }) {
    if (this.callId && parseInt(callId) !== this.callId) return;
    this.setStatus(WEBRTC_STATUS.DISCONNECTED);
    this.cleanup();
  }

  end() {
    if (this.callId != null && this.selfUserId != null) {
      this._signaling?.sendEnd(this.callId, parseInt(this.selfUserId));
    }
    this.setStatus(WEBRTC_STATUS.DISCONNECTED);
    this.cleanup();
  }

  mute(muted) {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach((t) => (t.enabled = !muted));
  }

  cleanup() {
    try {
      this.localStream?.getTracks().forEach((t) => t.stop());
    } catch {}
    try {
      if (this.pc) {
        this.pc.onicecandidate = null;
        this.pc.ontrack = null;
        this.pc.onconnectionstatechange = null;
        this.pc.close();
      }
    } catch {}
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
    }
    this.pc = null;
    this.localStream = null;
    this.callId = null;
    this.peerUserId = null;
    this.role = null;
    this.pendingOffer = null;
    this.pendingRemoteIce = [];
    this.pendingLocalIce = [];
  }
}

const webRtcCallService = new WebRtcCallService();
if (typeof window !== "undefined") {
  window.__webRtcCallService = webRtcCallService;
}
export default webRtcCallService;
